import { usePermissions } from '@minecraft.permissions';
import { useEffect, useRef, useState, useCallback } from 'react';
import { FrontChat, FrontChatCommandsEnum } from './types';

const SCRIPT_SRC = 'https://chat-assets.frontapp.com/v1/chat.bundle.js';
const SCRIPT_ID = 'front-chat-widget';

let frontChatPromise: Promise<FrontChat> | null = null;

export function useFrontChatWidget({
  chatId,
  element,
  shouldHide = false,
  bypassPermissionsCheck = false,
}: {
  chatId: string;
  element?: HTMLElement;
  shouldHide?: boolean;
  bypassPermissionsCheck?: boolean;
}) {
  const { ability } = usePermissions();

  const [frontChat, setFrontChat] = useState<FrontChat | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const initRef = useRef(false);
  const isVisible = useRef(false);

  const hasAccess = bypassPermissionsCheck ? true : ability.can('access', 'chat-support-widget');

  const canShow = hasAccess && !shouldHide;

  // Load the FrontChat script and get the global function
  useEffect(() => {
    let isMounted = true;

    if (!frontChatPromise) {
      frontChatPromise = new Promise<FrontChat>((resolve, reject) => {
        if (window.FrontChat) {
          resolve(window.FrontChat);
          return;
        }

        const script = document.createElement('script');
        script.src = SCRIPT_SRC;
        script.id = SCRIPT_ID;
        script.async = true;

        script.onload = () => {
          let attempts = 0;
          const maxRetries = 10;

          const check = () => {
            if (window.FrontChat) {
              resolve(window.FrontChat);
            } else if (attempts < maxRetries) {
              attempts += 1;
              setTimeout(check, 50);
            } else {
              reject(new Error('[FrontChat] Global not available'));
            }
          };

          check();
        };

        script.onerror = () => reject(new Error('[FrontChat] Script failed'));

        (element ?? document.body).appendChild(script);
      });
    }

    frontChatPromise
      .then((fc) => {
        if (!isMounted) return;
        setFrontChat(() => fc);
        setIsReady(true);
      })
      .catch(console.error);

    return () => {
      isMounted = false;
    };
  }, [element]);

  // initialize
  useEffect(() => {
    if (!frontChat || !chatId || initRef.current || !canShow) return;

    try {
      frontChat(FrontChatCommandsEnum.INIT, { chatId });
      initRef.current = true;
      isVisible.current = true;
      setIsInitialized(true);
    } catch (err) {
      console.error('[FrontChat] Init failed:', err);
    }
  }, [frontChat, chatId, canShow]);

  // show = INIT (only if not currently visible)
  const show = useCallback(() => {
    if (!frontChat || isVisible.current) return;

    frontChat(FrontChatCommandsEnum.INIT, { chatId });
    isVisible.current = true;
  }, [frontChat, chatId]);

  // hide = SHUTDOWN (only if currently visible)
  const hide = useCallback(() => {
    if (!frontChat || !isVisible.current) return;

    frontChat(FrontChatCommandsEnum.SHUTDOWN);
    isVisible.current = false;
  }, [frontChat]);

  // hide / show logic based on scroll position (only if initialized and allowed to show)
  useEffect(() => {
    if (!frontChat || !isInitialized) return () => null;

    if (!canShow) {
      hide();
      return () => null;
    }

    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const { scrollHeight } = document.documentElement;

      if (windowHeight >= scrollHeight - 30) {
        // No scroll → always show
        show();
      } else if (scrollTop + windowHeight >= scrollHeight - 30) {
        // Near bottom → hide
        hide();
      } else {
        // Otherwise → show
        show();
      }
    };

    // Run once initially
    handleScroll();

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [frontChat, isInitialized, canShow, show, hide]);

  return {
    isReady,
    isInitialized,
    show,
    hide,
    frontChat,
  };
}
